import { AxiosError } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FloorExceptionalClosingHours,
  FloorExceptionalClosingHoursResponse,
  ISearchBookingsParameters,
  JxtBookingStatusEnum,
} from '@jooxter/api';
import { defaultOnError } from '../../mutations/default-error';
import { useInvalidateFloorExceptionalClosingHoursQueries, useSplittedBookingSearch } from '../../queries';
import { IUseFloorExceptionalClosingHoursActions } from './types';
import { useOffCanvasStore } from '../../store';
import { unionBy } from 'lodash-es';
import { JxtDeleteEventModal, JxtLoader, generateToast } from '@jooxter/ui';
import {
  useCreateFloorExceptionalClosingHours,
  useDeleteFloorExceptionalClosingHours,
  useUpdateFloorExceptionalClosingHours,
  // eslint-disable-next-line import/namespace
} from '../../mutations/floor-exceptional-closing-hours';
import JxtFloorExceptionalClosingHoursDetails from '@jooxter/ui/src/components/JxtFloorExceptionalClosingHoursDetails';

export const useFloorExceptionalClosingHoursActions = ({
  floorId,
  range,
  additionalRange,
  floorExceptionalClosingHours,
  floorExceptionalClosingHoursDetails,
}: IUseFloorExceptionalClosingHoursActions) => {
  const { t } = useTranslation();
  const close = useOffCanvasStore((state) => state.close);
  const { mutate: mutationUpdate } = useUpdateFloorExceptionalClosingHours();
  const { mutate: mutationCreate } = useCreateFloorExceptionalClosingHours();
  const { mutate: mutationDelete } = useDeleteFloorExceptionalClosingHours();
  const { invalidateFloorExceptionalClosingHoursQueries } = useInvalidateFloorExceptionalClosingHoursQueries();
  const [showDeleteEventModal, setShowDeleteEventModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const searchBookingsBaseOptions: ISearchBookingsParameters = {
    floorId: floorId ? [floorId] : undefined,
    status: [JxtBookingStatusEnum.APPROVED, JxtBookingStatusEnum.REQUESTED],
  };

  const { bookings, isPending: isFetchingBookings } = useSplittedBookingSearch(
    { ...searchBookingsBaseOptions, from: range?.from, to: range?.to },
    !!floorId
  );

  const { bookings: additionalBookings, isPending: isFetchingAdditionalBookings } = useSplittedBookingSearch(
    {
      ...searchBookingsBaseOptions,
      from: additionalRange?.from,
      to: additionalRange?.to,
    },
    !!floorId
  );

  const filteredBookings = useMemo(() => {
    if (!range && !additionalRange) {
      return [];
    }
    if (range && bookings && !additionalRange) {
      return bookings;
    }
    if (additionalRange && additionalBookings && !range) {
      return additionalBookings;
    }
    return unionBy(bookings, additionalBookings, 'id');
  }, [range, additionalRange, bookings, additionalBookings]);

  const cancel = useCallback((): void => {
    if (floorExceptionalClosingHours && floorExceptionalClosingHoursDetails) {
      setShowDeleteEventModal(true);
    }
  }, [floorExceptionalClosingHours, floorExceptionalClosingHoursDetails]);

  const update = useCallback(
    (
      { id, payload }: { id: number; payload: FloorExceptionalClosingHoursResponse },
      onSuccess?: (floorExceptionalClosingHours: FloorExceptionalClosingHoursResponse) => void,
      onError?: (error: unknown) => void,
      onSettled?: () => void
    ) => {
      const error = (e: AxiosError) => {
        defaultOnError(e);
      };

      return mutationUpdate(
        { id, payload },
        {
          onSuccess: onSuccess,
          onError: onError || error,
          onSettled,
        }
      );
    },
    [isFetchingBookings, isFetchingAdditionalBookings, mutationUpdate]
  );

  const create = useCallback(
    (
      payload: FloorExceptionalClosingHours,
      onSuccess?: (floorExceptionalClosingHours: FloorExceptionalClosingHoursResponse) => void,
      onError?: (error: AxiosError, floorExceptionalClosingHours: FloorExceptionalClosingHoursResponse) => void,
      onSettled?: () => void
    ) => {
      mutationCreate(payload, { onSuccess, onError, onSettled });
    },
    [mutationCreate]
  );

  const onConfirmDeletion = useCallback(() => {
    setIsLoading(true);
    if (!floorExceptionalClosingHours?.id) {
      setShowDeleteEventModal(false);
      return;
    }

    const { id } = floorExceptionalClosingHours;

    const onSuccess = () => {
      setIsLoading(false);
      invalidateFloorExceptionalClosingHoursQueries([]);
      generateToast(t<string>('exceptional-closing-cancelled'), false);
    };

    const onError = () => {
      setIsLoading(false);
      generateToast(t<string>('exceptional-closing-cancellation-failed'), true);
    };

    const onSettled = () => {
      setIsLoading(false);
      invalidateFloorExceptionalClosingHoursQueries([id]);
      setShowDeleteEventModal(false);
      close();
    };

    mutationDelete(id, {
      onSuccess,
      onError,
      onSettled,
    });
  }, [
    floorExceptionalClosingHours,
    invalidateFloorExceptionalClosingHoursQueries,
    mutationDelete,
    close,
    t,
  ]);

  const deleteEventModal = useMemo(
    () => (
      <JxtDeleteEventModal
        titleKey="do-you-want-to-delete-this-exceptional-hour"
        onSubmit={onConfirmDeletion}
        onCancel={() => {
          setShowDeleteEventModal(false);
          close();
        }}
        show={showDeleteEventModal}
      >
        <div className="relative h-full w-full">
          {isLoading && (
            <div className="absolute flex items-center justify-center h-full w-full">
              <JxtLoader />
            </div>
          )}
          {floorExceptionalClosingHoursDetails && (
            <div className={`${isLoading ? 'opacity-20 pointer-events-none' : ''}`}>
              <JxtFloorExceptionalClosingHoursDetails showPlan={false} {...floorExceptionalClosingHoursDetails} />
            </div>
          )}
        </div>
      </JxtDeleteEventModal>
    ),
    [floorExceptionalClosingHours, showDeleteEventModal, onConfirmDeletion, close]
  );

  return {
    update,
    create,
    cancel,
    deleteEventModal,
    filteredBookings,
  };
};
